import * as Sentry from "@sentry/react";
import apiUtil from "../../../util/apiUtil";
import {
    IDomainSettings,
    IClientConceptMetricThreshold,
    ILocationMetricThreshold,
    IUserSettings,
    IIdealWeeklySalesPerEmployeeThresholdForHomeOffice,
    IClientConceptPage
} from "./applicationModels";

const applicationApi = {
    saveUserSettings(userSettings: IUserSettings): Promise<IUserSettings> {
        return apiUtil.postData("/api/client/saveUserSettings", userSettings);
    },

    trackPageViewOrAction(pathName: string, queryString?: string, postData?: string): void {
        let displayMode = "browser";
        const mqStandAlone = "(display-mode: standalone)";
        if (window.matchMedia(mqStandAlone).matches) {
            displayMode = "standalone";
        }

        apiUtil
            .postData("/api/usageTracking/TrackPageViewOrAction", {
                pathName,
                queryString,
                displayMode,
                postData
            })
            .catch((err: {status?: number}) => {
                // Usage tracking is non-critical — never let failures here create an
                // unhandled rejection. 401/403 are expected when the session has expired
                // or the user lacks permission.
                // TypeErrors represent network-level failures (e.g. Safari's "Load failed",
                // aborted requests, CORS blocks) and are not actionable — skip capturing.
                // Other failures (e.g. 500) are captured explicitly so they appear in Sentry
                // as handled errors rather than noisy unhandled rejections.
                if (err instanceof TypeError) return;
                if (err?.status !== 401 && err?.status !== 403) {
                    Sentry.captureException(err);
                }
            });
    },

    async getOpenTaskCount(clientUserId: string): Promise<number> {
        return apiUtil.fetchData(`/api/task/getOpenTaskCount/${clientUserId}`);
    },

    async getDomainSettings(): Promise<IDomainSettings> {
        return apiUtil.fetchData("/api/configuration/DomainSetting");
    },

    async getClientConceptMetricThresholds(
        clientConcept_PK: number
    ): Promise<IClientConceptMetricThreshold[]> {
        return apiUtil.fetchData(
            `/api/UIConfiguration/ClientConceptMetricThresholds/${clientConcept_PK}`
        );
    },

    async getIdealWeeklySalesPerEmployeeThreshold(
        clientConcept_PK: number
    ): Promise<IIdealWeeklySalesPerEmployeeThresholdForHomeOffice> {
        return apiUtil.fetchData(
            `/api/UIConfiguration/IdealWeeklySalesPerEmployeeThreshold/${clientConcept_PK}`
        );
    },

    async setIdealWeeklySalesPerEmployeeThreshold(
        idealWeeklySalesPerEmployeeThresholdForHomeOffice: IIdealWeeklySalesPerEmployeeThresholdForHomeOffice
    ): Promise<IIdealWeeklySalesPerEmployeeThresholdForHomeOffice> {
        return apiUtil.postData(
            "api/UIConfiguration/SetIdealWeeklySalesPerEmployeeThreshold",
            idealWeeklySalesPerEmployeeThresholdForHomeOffice
        );
    },

    updateClientConceptMetricThresholds(
        clientConceptMetricThreshold: IClientConceptMetricThreshold
    ): Promise<Response> {
        return apiUtil.postData(
            "/api/UIConfiguration/UpdateClientConceptMetricThresholds",
            clientConceptMetricThreshold
        );
    },

    deleteLocationMetricThreshold(
        clientConcept_PK: number,
        metricDefinitionId: number,
        locationPk: number
    ): Promise<Response> {
        return apiUtil.postData(
            `/api/UIConfiguration/DeleteLocationMetricThreshold/${clientConcept_PK}/${metricDefinitionId}/${locationPk}`
        );
    },

    async getLocationMetricThresholds(
        clientConcept_PK: number,
        businessUnitId: string,
        metricDefinitionId: string
    ): Promise<ILocationMetricThreshold> {
        return apiUtil.fetchData(
            `/api/UIConfiguration/LocationMetricThresholds/${clientConcept_PK}/${businessUnitId}/${metricDefinitionId}`
        );
    },

    updateLocationMetricThresholds(
        locationMetricThreshold: ILocationMetricThreshold,
        clientConcept_PK: number
    ): Promise<Response> {
        return apiUtil.postData(
            `/api/UIConfiguration/UpdateLocationMetricThresholds/${clientConcept_PK}`,
            locationMetricThreshold
        );
    },

    getClientConceptPages(): Promise<IClientConceptPage[]> {
        return apiUtil.fetchData("/api/UIConfiguration/ClientConceptPages");
    }
};

export default applicationApi;
